<template>
  <BaseSidebar
    :show-back-button="true"
    :back-text="$t('common.actions.close')"
    :show-footer="!isReadOnly && (!showDetails || editor.isEditMode.value)"
    @close="handleClose"
    @back="handleClose"
  >
    <div class="flex flex-col w-full h-full gap-y-2">
      <div class="border-b border-eblack-300">
        <ul class="flex flex-wrap -mb-px text-sm font-medium text-center text-eblack-950 bg-eblack-100/70 rounded-t-md pt-0.5 px-0.5">
          <li class="me-2">
            <button
              type="button"
              @click="tab = 1"
              :class="[tab == 1 ? 'bg-white border-b border-eorange-300' : 'border-transparent']"
              class="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 group"
            >
              <svg class="w-4 h-4 text-gray-400 me-2 group-hover:text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 18 20">
                <path d="M16 1h-3.278A1.992 1.992 0 0 0 11 0H7a1.993 1.993 0 0 0-1.722 1H2a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2Zm-3 14H5a1 1 0 0 1 0-2h8a1 1 0 0 1 0 2Zm0-4H5a1 1 0 0 1 0-2h8a1 1 0 1 1 0 2Zm0-5H5a1 1 0 0 1 0-2h2V2h4v2h2a1 1 0 1 1 0 2Z"/>
              </svg>{{ $t("services.auto.formulario") }}</button>
          </li>
          <li class="me-2">
            <button
              type="button"
              @click="tab = 2"
              :class="[tab == 2 ? 'bg-white border-b border-eorange-300' : 'border-transparent']"
              class="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg hover:text-gray-600 hover:border-gray-300 group"
            >
              <svg class="w-4 h-4 text-gray-400 me-2 group-hover:text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linejoin="round" stroke-width="2" d="M4 18V8a1 1 0 0 1 1-1h1.5l1.707-1.707A1 1 0 0 1 8.914 5h6.172a1 1 0 0 1 .707.293L17.5 7H19a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z"/>
                <path stroke="currentColor" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
              </svg>{{ $t("services.auto.multimedia") }}</button>
          </li>
        </ul>
      </div>

      <FormTab
        v-show="tab == 1"
        :service="service"
        :showDetails="showDetails"
        :errors="editor.errors.value"
        :availabilities="availability.items.value"
        :form="editor.form"
        :read-only="isReadOnly"
        :loadServices="loadServices"
        :closeModal="handleClose"
        ref="formTab"
        @show-availability-modal="handleShowAvailabilityModal"
        @edit-availability="handleEditAvailability"
        @erase-availability="handleEraseAvailability"
      />

      <MultimediaTab
        v-show="tab == 2"
        :service="service"
        :show-details="isReadOnly || showDetails"
        :errors="editor.errors.value"
        ref="multimediaTab"
        @photos-changed="onPhotosChanged"
        @refresh-photos="handleRefreshPhotos"
      />
    </div>

    <template #footer v-if="!isReadOnly && (!showDetails || editor.isEditMode.value)">
      <button
        type="button"
        class="bg-green-500 text-white px-2 py-1.5 rounded-md mb-2"
        :class="[editor.isSubmitting.value ? 'animate animate-bouncing' : '']"
        @click="handleSave"
      >
        {{ editor.buttonText.value }}
      </button>
    </template>
  </BaseSidebar>
</template>

<script setup>
import { i18n } from "@/i18n";
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useToast } from 'vue-toastification';
import BaseSidebar from '@/components/overlays/BaseSidebar.vue';
import FormTab from './FormTab.vue';
import MultimediaTab from './MultimediaTab.vue';
import AvailabilityConfigurationModal from '../../Calendars/components/AvailabilityConfigurationModal.vue';
import { useAvailability } from '@domains/scheduling/composables/useAvailability';
import { useServiceEditor } from '@domains/service/composables/useServiceEditor';
import { useServiceStore } from '@domains/service/stores/service.store';
import { useServiceChargeStore } from '@domains/charges/stores/service-charge.store';

const props = defineProps({
  service: { type: Object, default: null },
  showDetails: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  loadServices: { type: Function, required: true },
  overlayService: { type: Object, default: null }
});

const emit = defineEmits(['close', 'service-refreshed']);

const toast = useToast();
const serviceStore = useServiceStore();
const serviceChargeStore = useServiceChargeStore();
const formTab = ref(null);
const multimediaTab = ref(null);

const tab = ref(1);

const currentService = computed(() => props.service);
const isReadOnly = computed(() => props.readOnly || props.showDetails);

const availability = useAvailability({
  serviceId: props.service?.id,
  onSaveError: (err) => toast.error(err.message, { timeout: 4000, position: 'top-right' })
});

const editor = useServiceEditor({
  service: currentService,
  availability,
});

watch(() => props.service, (newService) => {
  if (newService?.id) {
    availability.load(newService.id);
  } else {
    availability.reset();
  }
}, { immediate: true });

function handleClose() {
  const hasChanges = !isReadOnly.value && (editor.isDirty.value || availability.hasChanges.value);
  if (hasChanges) {
    if (!confirm(i18n.global.t("services.auto.estas_seguro_de_que_deseas_cerrar_los_cambios_no"))) {
      return;
    }
  }
  resetForm();
  emit('close');
}

function resetForm() {
  editor.reset();
  formTab.value?.resetForm?.();
  multimediaTab.value?.resetForm?.();
  tab.value = 1;
  availability.reset();
  serviceChargeStore.$reset();
}

function handleShowAvailabilityModal(service) {
  if (isReadOnly.value) return;
  let checkDirty = null;
  props.overlayService?.open({
    component: AvailabilityConfigurationModal,
    type: 'modal',
    size: 'lg',
    position: 'center',
    props: {
      service,
      calendarId: availability.calendarId.value,
      modePolicy: getCreateAvailabilityModePolicy(),
      allowDraftOverrides: true,
      registerDirtyCheck: (fn) => { checkDirty = fn; }
    },
    events: {
      'create-calendar': ({ data }) => {
        availability.addAvailability(data);
        markDirty();
      },
      'create-override': ({ data }) => {
        availability.addOverride(data);
        markDirty();
      }
    },
    beforeClose: () => !checkDirty?.() || confirm(i18n.global.t("services.auto.cerrar_los_cambios_no_guardados_se_perderan")),
    onClose: () => {}
  });
}

function handleEditAvailability({ item, index }) {
  if (isReadOnly.value) {
    props.overlayService?.open({
      component: AvailabilityConfigurationModal,
      type: 'modal',
      size: 'lg',
      position: 'center',
      props: {
        service: props.service,
        editingAvailability: item,
        calendarId: availability.calendarId.value,
        readOnly: true,
        modePolicy: 'read-only',
      },
      events: {},
      beforeClose: () => true,
      onClose: () => {},
    });
    return;
  }

  let checkDirty = null;
  props.overlayService?.open({
    component: AvailabilityConfigurationModal,
    type: 'modal',
    size: 'lg',
    position: 'center',
    props: {
      service: props.service,
      editingAvailability: item,
      calendarId: availability.calendarId.value,
      modePolicy: getEditAvailabilityModePolicy(item),
      allowDraftOverrides: true,
      registerDirtyCheck: (fn) => { checkDirty = fn; }
    },
    events: {
      'create-calendar': ({ data }) => {
        availability.addAvailability(data);
        markDirty();
      },
      'create-override': ({ data }) => {
        availability.addOverride(data);
        markDirty();
      },
      // Modal emits: { serviceId, availabilityId, data: { flat schedule fields } }
      // Store needs: { schedule: [{ flat schedule fields }] }
      'update-calendar': ({ data }) => {
        availability.updateItem(index, { ...data, schedule: [data] });
        markDirty();
      },
      'update-override': ({ data }) => {
        availability.updateItem(index, data);
        markDirty();
      }
    },
    beforeClose: () => !checkDirty?.() || confirm(i18n.global.t("services.auto.cerrar_los_cambios_no_guardados_se_perderan")),
    onClose: () => {}
  });
}

function handleEraseAvailability(index) {
  if (isReadOnly.value) return;
  const result = availability.removeItem(index);
  if (result.success) {
    markDirty();
  } else if (result.error) {
    toast.error(result.error, { timeout: 4000, position: 'top-right' });
  }
}

async function handleSave() {
  if (isReadOnly.value) return;
  const wasEditMode = editor.isEditMode.value;

  try {
    const savedService = await editor.save();
    const bookingImpact = availability.lastBookingImpact?.value ?? null;
    if (!wasEditMode) {
      await serviceChargeStore.applyPendingCharges(savedService.id);
    }
    showSuccessAndClose(
      wasEditMode
        ? i18n.global.t("services.editor.updated_success")
        : i18n.global.t("services.editor.saved_success"),
      bookingImpact,
    );
  } catch (error) {
    handleSaveError(error);
  }
}

function handleSaveError(error) {
  const message =
    error instanceof Error
      ? error.message
      : i18n.global.t("services.validation.save_error");
  toast.error(message, { timeout: 4000, position: 'top-right' });

  if (editor.errors.value.some((e) => e.photo_storage_ids)) {
    tab.value = 2;
  }
}

function showSuccessAndClose(message, bookingImpact = null) {
  resetForm();
  props.loadServices();
  props.overlayService?.closeTop?.(true) || emit('close');
  toast.success(message, { timeout: 3000, position: 'top-right' });
  showBookingImpactWarning(bookingImpact);
}

function markDirty() {
  editor.markDirty();
}

function onPhotosChanged(ids) {
  editor.setPhotoStorageIds(ids);
}

function getCreateAvailabilityModePolicy() {
  const hasBaseAvailability = availability.items.value.some((item) => item?.type === 'availability');
  return hasBaseAvailability ? 'range-only' : 'all-and-range';
}

function getEditAvailabilityModePolicy(item) {
  return item?.type === 'override' ? 'range-locked' : 'all-locked';
}

function showBookingImpactWarning(bookingImpact) {
  const count = Number(bookingImpact?.impacted_count ?? 0);
  if (count <= 0) {
    return;
  }

  const examples = (bookingImpact.bookings || [])
    .slice(0, 3)
    .map((booking) => `${booking.date} ${booking.start_time}-${booking.end_time}`)
    .join(', ');
  const suffix = examples ? ` Ejemplos: ${examples}.` : '';

  toast.warning(
    `${count} reserva${count === 1 ? '' : 's'} existente${count === 1 ? '' : 's'} queda${count === 1 ? '' : 'n'} fuera de la disponibilidad actual. Las reservas se mantienen activas.${suffix}`,
    { timeout: false, position: 'top-right' },
  );
}

async function handleRefreshPhotos(serviceId) {
  try {
    const service = await serviceStore.fetchServiceById(serviceId);
    emit('service-refreshed', service);
  } catch (error) {
    toast.error(i18n.global.t("services.auto.error_refrescando_fotos"), { timeout: 3000, position: 'top-right' });
  }
}

let escapeHandler = null;
let backdropHandler = null;

onMounted(() => {
  escapeHandler = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      handleClose();
    }
  };
  document.addEventListener('keydown', escapeHandler, true);

  setTimeout(() => {
    const backdrop = document.querySelector('.overlay-backdrop');
    if (backdrop) {
      backdropHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      };
      backdrop.addEventListener('click', backdropHandler, true);
    }
  }, 100);
});

onBeforeUnmount(() => {
  if (escapeHandler) document.removeEventListener('keydown', escapeHandler, true);
  if (backdropHandler) {
    document.querySelector('.overlay-backdrop')?.removeEventListener('click', backdropHandler, true);
  }
});
</script>

<style lang="scss" scoped></style>
